// =============================================================================
// GiftCardApplyPanel — spend a physical gift card on a confirmed, unpaid
// booking at the counter (walk-in "Cadeaubon", unpaid booking at check-in).
// Scan the card or type its code; the card is debited at once
// (fn_admin_gift_card_apply_to_booking) and the booking's amount due drops to
// the rest. "Ongedaan maken" puts it back while nothing has been paid.
// =============================================================================
import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { formatError } from '../lib/errors';
import {
  applyGiftCardToBooking,
  formatEuro,
  giftCardApplyErrorText,
  parseGiftCardScan,
  removeGiftCardFromBooking,
  type GiftCardApplyResult,
} from '../lib/giftcard';

export function GiftCardApplyPanel({
  bookingId,
  onApplied,
  onRemoved,
  onCancel,
}: {
  bookingId: string;
  onApplied: (result: GiftCardApplyResult) => void;
  onRemoved?: () => void;
  onCancel?: () => void;
}) {
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<GiftCardApplyResult | null>(null);

  const parsed = parseGiftCardScan(code);

  async function apply(raw: string) {
    const c = parseGiftCardScan(raw);
    if (!c) {
      setError('Dat is geen geldige cadeauboncode. Controleer de code op de kaart.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await applyGiftCardToBooking(bookingId, c);
      if (res.error) {
        setError(giftCardApplyErrorText(res.error));
        return;
      }
      setApplied(res);
      onApplied(res);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  async function undo() {
    setBusy(true);
    setError(null);
    try {
      const res = await removeGiftCardFromBooking(bookingId);
      if (res.error) {
        setError(giftCardApplyErrorText(res.error));
        return;
      }
      setApplied(null);
      setCode('');
      onRemoved?.();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  if (applied) {
    return (
      <div className="giftcard-apply">
        <div className="walkin-success-title">
          ✓ Cadeaubon: {formatEuro(applied.amount_cents ?? 0)}
        </div>
        <div className="muted">
          {applied.fully_paid
            ? 'De boeking is volledig betaald.'
            : `Nog te betalen: ${formatEuro(applied.remaining_cents ?? 0)}`}
          {' · '}saldo op de kaart: {formatEuro(applied.balance_after_cents ?? 0)}
        </div>
        {error ? <div className="error-card">{error}</div> : null}
        {!applied.fully_paid ? (
          <button className="btn-link" disabled={busy} onClick={() => void undo()}>
            Ongedaan maken
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="giftcard-apply">
      <div className="walkin-field">
        <span>Cadeaubon</span>
        <form
          className="giftcard-apply-row"
          onSubmit={(e) => {
            e.preventDefault();
            void apply(code);
          }}
        >
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="XXXX-XXXX-XXXX"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" className="btn-secondary" onClick={() => setScanning(true)}>
            Scan
          </button>
          <button type="submit" className="walkin-primary-btn" disabled={busy || !parsed}>
            {busy ? 'Bezig…' : 'Gebruiken'}
          </button>
        </form>
      </div>
      {error ? <div className="error-card">{error}</div> : null}
      {onCancel ? (
        <button className="btn-link" disabled={busy} onClick={onCancel}>Terug</button>
      ) : null}
      {scanning ? (
        <QrScanModal
          onClose={() => setScanning(false)}
          onScan={(text) => {
            setScanning(false);
            setCode(text);
            void apply(text);
          }}
        />
      ) : null}
    </div>
  );
}

// Minimal camera scanner for the card's QR (the Scan screen has the full one).
function QrScanModal({ onScan, onClose }: { onScan: (text: string) => void; onClose: () => void }) {
  const regionId = 'giftcard-qr-region';
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);

  useEffect(() => {
    const scanner = new Html5Qrcode(regionId, {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    });
    let startPromise: Promise<unknown> | undefined;
    startPromise = scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: (vw, vh) => { const e = Math.floor(Math.min(vw, vh) * 0.75); return { width: e, height: e }; } },
        (text) => {
          if (done.current) return;
          if (!parseGiftCardScan(text)) return;
          done.current = true;
          onScan(text);
        },
        () => { /* no code in this frame */ },
      )
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    return () => {
      void (async () => {
        try { await startPromise; } catch { /* ignore */ }
        try { if (scanner.isScanning) await scanner.stop(); } catch { /* ignore */ }
        try { scanner.clear(); } catch { /* ignore */ }
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ only */ }}>
      <div className="dep-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div><h2>Scan de cadeaubon</h2></div>
          <button className="btn-link dep-modal-close" onClick={onClose} aria-label="Sluiten">✕</button>
        </header>
        <div className="dep-modal-body">
          <div id={regionId} className="scan-viewfinder" />
          {error ? <div className="error-card">{error}</div> : null}
        </div>
      </div>
    </div>
  );
}
